@if (article$ | withLoading | async; as article) {
    <div>
        @if (article.loading || loading) {
            <div class="flex min-h-screen flex-col items-center justify-center">
                <app-loader color="#EE5769"></app-loader>
            </div>
        }
        @if (!article.loading && !loading && !article.value) {
            <app-not-found></app-not-found>
        }
        @if (!article.loading && !loading && article.value) {
            <article>
                <div class="relative">
                    <div
                        class="aspect-h-1 aspect-w-1 overflow-hidden md:aspect-w-2 2xl:aspect-w-3"
                        id="blogFeaturedImage">
                        <!-- Lazy Picture with clipping applied -->
                        <app-lazy-picture
                            [preload]="true"
                            [lazy]="false"
                            [configurations]="
                                featuredImageConfiguration | async
                            "
                            class="h-full w-full">
                        </app-lazy-picture>
                    </div>
                    <svg
                        class="absolute -bottom-px h-auto w-full"
                        xmlns="http://www.w3.org/2000/svg"
                        width="1920"
                        height="83"
                        viewBox="0 0 1920 83">
                        <path
                            d="M1920,69.38C1608.9,24.2,1290.79.81,967.25.81S315.72,24.94,0,71.51v11.8h1920v-13.93Z"
                            fill="#fff" />
                    </svg>
                </div>
                <!-- Grid Layout -->
                <div
                    class="blogBackgroundImage mb-0 grid grid-cols-1 gap-4 px-4 md:grid-cols-12 md:gap-2 md:px-5">
                    <!-- Left Section (Hidden on Mobile) -->
                    <aside
                        class="relative hidden md:col-span-3 md:block md:px-2">
                        <div class="flex h-full justify-center">
                            <app-knowledge-article-toc
                                [article]="article$ | async"
                                class="sticky top-1 max-h-[calc(100vh-0.5rem)] overflow-y-auto"></app-knowledge-article-toc>
                        </div>
                    </aside>

                    <!-- Middle Section -->
                    <div class="col-span-12 md:col-span-6 md:px-2" id="blogTop">
                        <div class="mx-auto max-w-4xl">
                            <header>
                                <!-- Bread Crumbs -->
                                <div class="max-w-md sm:max-w-full">
                                    <app-knowledge-article-breadcrumbs
                                        [article]="article.value"
                                        [destination]="destination$ | async">
                                    </app-knowledge-article-breadcrumbs>
                                </div>

                                <!-- Article Title -->

                                <div class="w-5/6 md:w-full">
                                    <h1
                                        class="font-heading text-3xl font-bold text-darkgrey md:text-4xl">
                                        {{ article.value?.title }}
                                    </h1>
                                </div>

                                <!-- Date for Mobile -->
                                <div
                                    class="block flex items-center pr-2 pt-4 md:hidden">
                                    <span class="ml-1 font-heading">{{
                                        article.value.sys.publishedAt | date
                                    }}</span>
                                </div>

                                <!-- Article description -->
                                <div class="pb-4 pt-6">
                                    <p
                                        class="font-serif text-xl leading-relaxed tracking-wide text-darkgrey"
                                        [innerHTML]="
                                            article.value?.description
                                        "></p>
                                </div>

                                <!-- Author, Date, Reading Time -->
                                <div
                                    class="mt-10 flex flex-wrap items-center justify-start text-base md:mb-8 md:mt-4">
                                    @if (author$ | async) {
                                        <div class="md:pr-2">
                                            <app-knowledge-article-author
                                                [author]="author$ | async">
                                            </app-knowledge-article-author>
                                        </div>

                                        <div
                                            class="mx-2 hidden h-full border-l border-darkgrey md:block">
                                            &nbsp;
                                        </div>
                                    }

                                    <div
                                        class="flex hidden items-center pr-2 md:block">
                                        <span class="ml-1 font-heading">
                                            {{
                                                article.value.sys.publishedAt
                                                    | date
                                            }}
                                        </span>
                                    </div>

                                    <div
                                        class="mx-2 h-full border-l border-darkgrey">
                                        &nbsp;
                                    </div>

                                    <div class="flex items-center pr-2">
                                        <fa-icon [icon]="faClock"></fa-icon>
                                        <span class="ml-2">
                                            <span class="md:inline">
                                                <p class="font-heading">
                                                    {{ readingTime | async }}
                                                    min read
                                                </p>
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </header>
                            <aside>
                                <div class="relative my-10 block md:hidden">
                                    <app-knowledge-article-toc
                                        [article]="article$ | async">
                                    </app-knowledge-article-toc>
                                </div>
                            </aside>
                            <!-- Article Content -->
                            <app-rich-text-document
                                [styleOverride]="
                                    contentfulDynamicStylesOverrides
                                "
                                [componentOverride]="
                                    contentfulDynamicComponentOverrides
                                "
                                [document]="article.value?.body.json"
                                [resolvedLinks]="resolvedLinks | async">
                            </app-rich-text-document>
                            <!-- <app-trustpilot-badge></app-trustpilot-badge> -->
                        </div>
                    </div>

                    <!-- Right Section (Stacked Below Content on Mobile) -->
                    <aside
                        class="order-last col-span-12 hidden text-center md:order-none md:col-span-3 md:block md:text-left">
                        <div
                            class="flex h-1/3 flex-col items-center justify-start py-10">
                            <app-lazy-asset
                                [configuration]="balloonConfiguration"
                                [additionalClasses]="['h-auto', 'w-1/2']">
                            </app-lazy-asset>
                        </div>
                        <div
                            class="flex h-1/3 flex-col items-center justify-start py-10">
                            <app-lazy-asset
                                [configuration]="compassConfiguration"
                                [additionalClasses]="['h-auto', 'w-1/2']">
                            </app-lazy-asset>
                        </div>
                        <div
                            class="flex h-1/3 flex-col items-center justify-start py-10">
                            <app-lazy-asset
                                [configuration]="balloonConfiguration"
                                [additionalClasses]="['h-auto', 'w-1/2']">
                            </app-lazy-asset>
                        </div>
                    </aside>
                </div>
                <footer>
                    <!-- Divider -->
                    <div class="my-20 grid grid-cols-12 md:my-28">
                        <div
                            class="col-span-10 col-start-2 w-full border-t-2 border-[#EAEAEA]">
                            &nbsp;
                        </div>
                    </div>

                    <div
                        class="mb-20 flex w-full flex-col items-center justify-center">
                        <app-knowledge-read-next
                            [article]="article$ | async"
                            class="w-full max-w-[700px]"></app-knowledge-read-next>
                    </div>

                    <!-- Travel Options -->
                    <app-knowledge-variation-cards
                        class="block"
                        [slim]="false"
                        [destination]="destination$ | async">
                    </app-knowledge-variation-cards>

                    <!-- Back To Top -->
                    <div
                        class="flex cursor-pointer flex-col items-center justify-center py-10 md:py-20"
                        (click)="scrollTo('blogTop')">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                            class="mb-2 h-6 w-6">
                            <path
                                d="M233.4 105.4c12.5-12.5 32.8-12.5 45.3 0l192 192c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L256 173.3 86.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l192-192z" />
                        </svg>
                        <p class="text-sm md:text-base">Scroll To Top</p>
                    </div>

                    <app-lazy-asset
                        [configuration]="footerConfiguration"
                        [additionalClasses]="['h-auto', 'w-full']">
                    </app-lazy-asset>
                </footer>

                <app-json-ld [json]="schemaArticle | async"></app-json-ld>
                <app-lead-magnet-exit-intent
                    [leadMagnet]="
                        (article$ | async).exitIntentPopup
                    "></app-lead-magnet-exit-intent>
            </article>
        }
    </div>
}
